/*
 * Copyright Mimic Networks, Inc. 2024.
 */

import { ArrowRightOutlined } from '@ant-design/icons';
import { Checkbox, Form, Grid } from 'antd';
import { FormEventHandler, useEffect, useState } from 'react';

import { validationErrors } from '@/components/Forms/validationErrors';
import { MimicLogo } from '@/components/MimicLogo';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Button } from '@/primitives/Button';
import { Input } from '@/primitives/Input';
import { Space } from '@/primitives/Space';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';

const REMEMBERED_EMAIL_KEY = 'mimic_remembered_email';

const { useBreakpoint } = Grid;

export type LogInProps = {
  triggerLogIn: (emailAddress: string) => void;
  emailMismatch: string | null;
  error?: boolean;
};

type LoginFormFields = {
  email: string;
};

function getRememberedEmail(): string | null {
  return localStorage.getItem(REMEMBERED_EMAIL_KEY);
}

export function LogIn({ triggerLogIn, emailMismatch, error }: LogInProps) {
  const { t } = useMimicTranslation('logIn');
  const rememberedEmail = getRememberedEmail();
  const [rememberMe, setRememberMe] = useState<boolean>(!!rememberedEmail);
  const [form] = Form.useForm();
  const screens = useBreakpoint();

  const watchedEmail = Form.useWatch('email', form);
  // Use rememberedEmail as fallback for first render before form initializes
  const emailAddress = watchedEmail ?? rememberedEmail;
  const submitEnabled = !!emailAddress;

  useEffect(() => {
    if (emailMismatch) {
      form.setFields(validationErrors<LoginFormFields>({ email: t('emailMismatch') }));
      form.setFieldValue('email', emailMismatch);
    }
    if (error) {
      form.setFields(validationErrors<LoginFormFields>({ email: t('unexpectedError') }));
    }
  }, [emailMismatch, error, form, t]);

  const handleSubmit: FormEventHandler<HTMLFormElement> = () => {
    if (rememberMe) {
      localStorage.setItem(REMEMBERED_EMAIL_KEY, emailAddress);
    } else {
      localStorage.removeItem(REMEMBERED_EMAIL_KEY);
    }
    triggerLogIn(emailAddress);
  };

  const containerStyle = {
    padding: screens.sm ? '42px' : '34px',
    border: screens.sm ? '1px solid #EFEFEE' : 'none',
    width: 600,
  };

  return (
    <div style={containerStyle}>
      <Space dir="vertical" size="lg" width="100%">
        <MimicLogo width="8rem" height="3rem" color="#222121" />
        <Space dir="vertical" size="sm">
          <Title style={{ margin: 0 }}>{t('welcome')}</Title>
          <Text size="xl" lineHeight="1.75rem">
            {t('instructions')}
          </Text>
        </Space>
        <Form form={form} onFinish={handleSubmit}>
          <Form.Item name="email" initialValue={rememberedEmail} style={{ marginBottom: '1.5rem' }}>
            <Input
              placeholder={t('email')}
              autoFocus
              dataTestId="email-input"
              type="email"
              style={{
                padding: '0.5625rem 0.875rem',
                fontSize: '1.1875rem',
                lineHeight: '1.75rem',
                backgroundColor: '#ffffff',
              }}
              allowClear
            />
          </Form.Item>
          <Button
            className="login-button"
            type="primary"
            htmlType="submit"
            dataTestId="trigger-log-in"
            block
            style={{ height: '48px' }}
            disabled={!submitEnabled}
          >
            {t('continue')}
            <ArrowRightOutlined />
          </Button>
          <Form.Item style={{ marginTop: '0.5rem', marginBottom: 0 }}>
            <Checkbox
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              data-testid="remember-me-checkbox"
            >
              {t('rememberMe')}
            </Checkbox>
          </Form.Item>
        </Form>
      </Space>
    </div>
  );
}
